import { Injectable } from "@angular/core";
import { CanActivate, Router } from "@angular/router";
import { AgreementSigningStatus, IdentityVerificationAction, IdentityVerificationFacade, IdentityVerificationResultStateHelper, SsoAccountCheckoutState, SsoAccountData, SsoAccountFacade } from "common";
import { combineLatest, Observable, of } from "rxjs";
import { switchMap, take } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AccountSetupGuard implements CanActivate {
  constructor(
    private readonly router: Router,
    private readonly ssoAccountFacade: SsoAccountFacade,
    private readonly identityVerificationFacade: IdentityVerificationFacade
  ) { }

  canActivate(): Observable<boolean> {
    return combineLatest([
      this.ssoAccountFacade.getCurrentSsoAccount$(),
      this.ssoAccountFacade.getSsoAccountCheckoutState$()
    ])
      .pipe(
        take(1),
        switchMap(([ssoAccount, checkoutState]) => this.validate(ssoAccount, checkoutState))
      )
  }

  private validate(currentSsoAccount: SsoAccountData, checkoutState: SsoAccountCheckoutState): Observable<boolean> {
    if (!this.hasAccessToRestrictedResources(currentSsoAccount) || checkoutState?.identityVerificationAction === IdentityVerificationAction.Skip) {
      this.router.navigate(["home"]);
      return of(false);
    }

    return combineLatest([this.identityVerificationFacade.isVerificationFailedWithNoRetry(), this.identityVerificationFacade.isVerificationExpiredNoRetry()])
      .pipe(
        take(1),
        switchMap(([isFailed, isExpired]) => this.validateRedirectOnPageWithNoRetry(isFailed, isExpired, checkoutState.errorMessage))
      );
  }

  private validateRedirectOnPageWithNoRetry(isFailed: boolean, isExpired: boolean, errorMessage: string): Observable<boolean> {
    const title = isExpired ? "Verification expired" : undefined;
    if(isFailed || isExpired) {
      IdentityVerificationResultStateHelper.set({ title, message: errorMessage });
      this.router.navigate(['account', 'setup', 'verify-identity-result'], {
          replaceUrl: true,
        });

        return of(false);
      }

    return of(true);
  }

  private hasAccessToRestrictedResources(currentSsoAccount: SsoAccountData): boolean {
    const hasPendingLoan = currentSsoAccount?.loans?.some(l => l.agreementState?.agreementSigningStatus === AgreementSigningStatus.PendingCurrentUser);
    const hasPendingApp = currentSsoAccount?.pendingApplications?.length > 0;
    const hasAnyLoan = currentSsoAccount?.loans?.length > 0;
    return hasPendingLoan || hasPendingApp || hasAnyLoan;
  }
}
